import React, { useMemo } from 'react';
import {
  Paper, List, ListItemButton, ListItemText, Stack, Box, Typography,
  alpha, useTheme, Divider, LinearProgress, Chip
} from '@mui/material';
import {
  HourglassEmpty, RadioButtonUnchecked, CheckCircle, ErrorOutline, PendingActions
} from '@mui/icons-material';
import { SessionClass, PromotionRunStatusByClass } from '../../types/sessions';

interface ClassStatusListProps {
  classes: SessionClass[];
  perClass: PromotionRunStatusByClass[];
  selectedClassId: string | null;
  onSelect: (classId: string) => void;
}

type ClassUiState = 'not_started' | 'decisions_in_progress' | 'ready_to_commit' | 'partially_committed' | 'fully_committed' | 'failed';

interface ClassRowData {
  cls: SessionClass;
  decided: number;
  undecided: number;
  committed: number;
  failed: number;
  total: number;
  state: ClassUiState;
}

const stateMeta: Record<ClassUiState, { icon: React.ElementType; color: string; label: string }> = {
  not_started:           { icon: RadioButtonUnchecked, color: '',            label: 'Not started' },
  decisions_in_progress: { icon: PendingActions,       color: 'warning',     label: 'In progress' },
  ready_to_commit:       { icon: HourglassEmpty,       color: 'primary',     label: 'Ready to commit' },
  partially_committed:   { icon: PendingActions,       color: 'info',        label: 'Partially committed' },
  fully_committed:       { icon: CheckCircle,          color: 'success',     label: 'Fully committed' },
  failed:                { icon: ErrorOutline,         color: 'error',       label: 'Has failures' }
};

const computeRows = (
  classes: SessionClass[],
  perClass: PromotionRunStatusByClass[]
): ClassRowData[] => {
  const byId = new Map(perClass.map(p => [String(p.classId), p]));
  return classes.map(cls => {
    const p = byId.get(String(cls._id));
    const decided   = p?.totalDecided   ?? 0;
    const undecided = p?.totalUndecided ?? 0;
    const committed = p?.totalCommitted ?? 0;
    const failed    = p?.totalFailed    ?? 0;
    const total     = decided + undecided + committed;

    let state: ClassUiState;
    if (failed > 0)                                state = 'failed';
    else if (total === 0)                          state = 'not_started';
    else if (committed === 0 && decided > 0)       state = 'ready_to_commit';
    else if (committed === 0 && undecided > 0)     state = 'decisions_in_progress';
    else if (committed > 0 && (decided + undecided) > 0) state = 'partially_committed';
    else                                           state = 'fully_committed';

    return { cls, decided, undecided, committed, failed, total, state };
  })
  // Only show classes with at least one source enrollment for this run.
  .filter(r => r.total > 0);
};

const ClassStatusList: React.FC<ClassStatusListProps> = ({
  classes, perClass, selectedClassId, onSelect
}) => {
  const theme = useTheme();
  const rows = useMemo(() => computeRows(classes, perClass), [classes, perClass]);

  if (rows.length === 0) {
    return (
      <Paper variant="outlined" sx={{ p: 3, borderRadius: 2, textAlign: 'center', borderStyle: 'dashed' }}>
        <Typography variant="body2" color="text.secondary">
          No classes have students to promote in this run.
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
      <Box sx={{ px: 2, py: 1.5, bgcolor: alpha(theme.palette.text.primary, 0.025), borderBottom: '1px solid', borderColor: 'divider' }}>
        <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 0.6 }}>
          Classes
        </Typography>
      </Box>
      <List dense disablePadding>
        {rows.map((row, idx) => {
          const meta = stateMeta[row.state];
          const Icon = meta.icon;
          const isSelected = selectedClassId === String(row.cls._id);
          const pct = row.total ? Math.round((row.committed / row.total) * 100) : 0;

          return (
            <React.Fragment key={String(row.cls._id)}>
              <ListItemButton
                selected={isSelected}
                onClick={() => onSelect(String(row.cls._id))}
                sx={{
                  py: 1.5,
                  px: 2,
                  borderLeft: '3px solid',
                  borderLeftColor: isSelected ? 'primary.main' : 'transparent',
                  '&.Mui-selected': { bgcolor: alpha(theme.palette.primary.main, 0.08) }
                }}
              >
                <ListItemText
                  primary={
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                      <Typography variant="subtitle2" fontWeight={600}>{row.cls.name}</Typography>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <Icon sx={{ fontSize: 14, color: meta.color ? `${meta.color}.main` : 'text.disabled' }} />
                        <Typography variant="caption" color="text.secondary">{meta.label}</Typography>
                      </Stack>
                    </Stack>
                  }
                  secondary={
                    <Box sx={{ mt: 0.5 }}>
                      <LinearProgress
                        variant="determinate"
                        value={pct}
                        sx={{
                          height: 4, borderRadius: 2, mb: 0.75,
                          bgcolor: alpha(theme.palette.text.primary, 0.06),
                          '& .MuiLinearProgress-bar': { borderRadius: 2 }
                        }}
                      />
                      <Stack direction="row" spacing={1} flexWrap="wrap">
                        <Chip size="small" variant="outlined" sx={{ height: 18, fontSize: 10.5, fontWeight: 500 }} label={`Total ${row.total}`} />
                        {row.committed > 0 && (
                          <Chip size="small" color="success" variant="outlined" sx={{ height: 18, fontSize: 10.5, fontWeight: 500 }} label={`${row.committed} committed`} />
                        )}
                        {row.decided > 0 && (
                          <Chip size="small" color="primary" variant="outlined" sx={{ height: 18, fontSize: 10.5, fontWeight: 500 }} label={`${row.decided} decided`} />
                        )}
                        {row.undecided > 0 && (
                          <Chip size="small" color="warning" variant="outlined" sx={{ height: 18, fontSize: 10.5, fontWeight: 500 }} label={`${row.undecided} undecided`} />
                        )}
                        {row.failed > 0 && (
                          <Chip size="small" color="error" variant="outlined" sx={{ height: 18, fontSize: 10.5, fontWeight: 500 }} label={`${row.failed} failed`} />
                        )}
                      </Stack>
                    </Box>
                  }
                  primaryTypographyProps={{ component: 'div' }}
                  secondaryTypographyProps={{ component: 'div' }}
                />
              </ListItemButton>
              {idx < rows.length - 1 && <Divider component="li" />}
            </React.Fragment>
          );
        })}
      </List>
    </Paper>
  );
};

export default ClassStatusList;
